(function () {
  'use strict';

  const FORM_SELECTOR = 'form[data-product-form]';
  const GROUP_SELECTOR = '[data-variant-option-group]';
  const OPTION_INPUT_SELECTOR = '[data-variant-option-input]';
  const VARIANT_SELECT_SELECTOR = '[data-product-select]';
  const VARIANT_DATA_SELECTOR = '[data-product-variants]';
  const states = new WeakMap();

  function variantOptions(variant) {
    if (Array.isArray(variant.options)) {
      return variant.options.map(String);
    }

    return [variant.option1, variant.option2, variant.option3]
      .filter(function (value) {
        return value !== null && value !== undefined;
      })
      .map(String);
  }

  function getRoot(form) {
    return form.closest('[data-product]') || document;
  }

  function getGroups(form) {
    return Array.from(form.querySelectorAll(GROUP_SELECTOR)).sort(function (
      first,
      second,
    ) {
      return (
        Number(first.dataset.optionPosition || 0) -
        Number(second.dataset.optionPosition || 0)
      );
    });
  }

  function getGroupValue(group) {
    const checked = group.querySelector('input[type="radio"]:checked');
    const select = group.querySelector('select');

    return checked ? checked.value : select ? select.value : null;
  }

  function getSelectedOptions(form) {
    return getGroups(form).map(getGroupValue);
  }

  function setGroupValue(group, value) {
    const select = group.querySelector('select');

    if (select) {
      select.value = String(value);
    }

    group.querySelectorAll('input[type="radio"]').forEach(function (input) {
      input.checked = String(input.value) === String(value);
    });

    const selectedLabel = group.querySelector('[data-selected-option-value]');

    if (selectedLabel) {
      selectedLabel.textContent = value;
    }
  }

  function getVariantOption(form, variantId) {
    const variantSelect = form.querySelector(VARIANT_SELECT_SELECTOR);

    if (!variantSelect) {
      return null;
    }

    return (
      Array.from(variantSelect.options).find(function (option) {
        return String(option.value) === String(variantId);
      }) || null
    );
  }

  function getVariantById(variants, variantId) {
    return (
      variants.find(function (variant) {
        return String(variant.id) === String(variantId);
      }) || null
    );
  }

  function getExactVariant(variants, options) {
    if (options.some(function (value) { return value === null; })) {
      return null;
    }

    return (
      variants.find(function (variant) {
        const candidateOptions = variantOptions(variant);

        return options.every(function (value, index) {
          return String(candidateOptions[index]) === String(value);
        });
      }) || null
    );
  }

  function isPreorder(form, variant) {
    const option = getVariantOption(form, variant.id);
    return option ? option.dataset.preorder === 'true' : false;
  }

  function isOrderable(form, variant) {
    return Boolean(variant && (variant.available || isPreorder(form, variant)));
  }

  function findFallbackVariant(form, changedPosition, changedValue) {
    const state = states.get(form);
    const selected = getSelectedOptions(form);
    const candidates = state.variants.filter(function (variant) {
      return (
        String(variantOptions(variant)[changedPosition]) === String(changedValue)
      );
    });

    if (!candidates.length) {
      return null;
    }

    return (
      candidates.find(function (variant) {
        const options = variantOptions(variant);

        return selected.every(function (value, index) {
          return index === changedPosition || String(options[index]) === String(value);
        });
      }) ||
      candidates.find(function (variant) {
        return isOrderable(form, variant);
      }) ||
      candidates[0]
    );
  }

  function resolveVariantForInput(form, input) {
    const state = states.get(form);

    if (!state) {
      return null;
    }

    const position = Math.max(
      0,
      Number(input.dataset.optionPosition || 1) - 1,
    );
    const intendedOptions = getSelectedOptions(form);
    intendedOptions[position] = input.value;
    const exact = getExactVariant(state.variants, intendedOptions);

    return exact || findFallbackVariant(form, position, input.value);
  }

  function normalizeSelectionBeforeThemeHandler(event) {
    const input = event.target.closest(OPTION_INPUT_SELECTOR);

    if (!input) {
      return;
    }

    const form = input.closest(FORM_SELECTOR);
    const variant = form && resolveVariantForInput(form, input);

    if (!variant) {
      return;
    }

    const values = variantOptions(variant);

    getGroups(form).forEach(function (group, index) {
      setGroupValue(group, values[index]);
    });

    const variantSelect = form.querySelector(VARIANT_SELECT_SELECTOR);

    if (variantSelect) {
      variantSelect.value = String(variant.id);
    }

    applyVariant(form, variant, { updateUrl: true });
    event.stopImmediatePropagation();
  }

  function setChoiceAvailability(choice, enabled) {
    choice.disabled = !enabled;
    choice.setAttribute('aria-disabled', String(!enabled));

    const wrapper =
      choice.closest('.variant-radio-option') || choice.closest('label');

    if (wrapper) {
      wrapper.classList.toggle('opacity-40', !enabled);
      wrapper.classList.toggle('is-unavailable', !enabled);
    }
  }

  function updateOptionAvailability(form, activeVariant) {
    const state = states.get(form);
    const activeOptions = variantOptions(activeVariant);

    getGroups(form).forEach(function (group, position) {
      const choices = group.querySelectorAll('option, input[type="radio"]');

      choices.forEach(function (choice) {
        const possible = state.variants.some(function (variant) {
          const options = variantOptions(variant);

          if (String(options[position]) !== String(choice.value)) {
            return false;
          }

          for (let prior = 0; prior < position; prior += 1) {
            if (String(options[prior]) !== String(activeOptions[prior])) {
              return false;
            }
          }

          return true;
        });

        setChoiceAvailability(choice, possible);
      });
    });
  }

  function formatMoney(cents) {
    const format =
      (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const token = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(token);
    const value = Number(cents || 0);

    if (!match) {
      return (value / 100).toFixed(2);
    }

    function withSeparators(decimals, thousands, decimal) {
      const fixed = (value / 100).toFixed(decimals).split('.');
      const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed[1] ? whole + decimal + fixed[1] : whole;
    }

    const replacements = {
      amount: withSeparators(2, ',', '.'),
      amount_no_decimals: withSeparators(0, ',', '.'),
      amount_with_comma_separator: withSeparators(2, '.', ','),
      amount_no_decimals_with_comma_separator: withSeparators(0, '.', ','),
    };

    return format.replace(token, replacements[match[1]] || replacements.amount);
  }

  function updatePrice(root, variant) {
    root.querySelectorAll('[data-product-price]').forEach(function (element) {
      element.textContent = formatMoney(variant.price);
    });

    root.querySelectorAll('[data-compare-price]').forEach(function (element) {
      const show = Number(variant.compare_at_price) > Number(variant.price);
      element.textContent = show ? formatMoney(variant.compare_at_price) : '';
      element.classList.toggle('hidden', !show);
    });
  }

  function updateSku(root, variant) {
    root.querySelectorAll('[data-variant-sku]').forEach(function (element) {
      element.textContent = variant.sku || '';
    });
  }

  function updateUrl(variant) {
    if (!window.history || !window.history.replaceState) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(
      Object.assign({}, window.history.state || {}, {
        variantId: String(variant.id),
      }),
      '',
      url.toString(),
    );
  }

  function updateOnlineAvailability(form, variant, preorder) {
    const shell = form.closest('[data-product-form-shell]');
    const availability =
      (shell &&
        shell.nextElementSibling &&
        shell.nextElementSibling.querySelector('[data-online-availability]')) ||
      document.querySelector('[data-online-availability]');

    if (!availability) {
      return;
    }

    const title = availability.querySelector(
      '[data-online-availability-title]',
    );
    const description = availability.querySelector(
      '[data-online-availability-description]',
    );
    const threshold = Number(availability.dataset.inventoryThreshold || 0);
    const inventory = Number(variant.inventory_quantity || 0);
    let status = 'available';

    if (preorder) {
      status = 'preorder';
    } else if (!variant.available) {
      status = 'out_of_stock';
    } else if (variant.inventory_management && inventory <= threshold) {
      status = 'limited';
    }

    availability.dataset.onlineStatus = status;

    if (!title || !description) {
      return;
    }

    const strings = availability.dataset;

    if (status === 'preorder') {
      title.textContent = strings.preorderTitle || 'Pre-order available';
      description.textContent =
        strings.preorderText || 'Order now — ships when available';
    } else if (status === 'out_of_stock') {
      title.textContent = strings.onlineTitle || 'Online availability';
      description.textContent =
        strings.unavailableText || 'Currently unavailable to order online';
    } else if (status === 'limited') {
      title.textContent = strings.onlineTitle || 'Online availability';
      description.textContent =
        strings.limitedText || 'Only a few items are available online';
    } else {
      title.textContent = strings.onlineTitle || 'Online availability';
      description.textContent =
        strings.availableText || 'Available to order online';
    }
  }

  function updateQuantity(form, variant, preorder) {
    const quantityInput = form.querySelector('[data-quantity-input]');
    const submitQuantity = form.querySelector('[data-submit-quantity]');
    const variantOption = getVariantOption(form, variant.id);
    const continueSelling =
      variantOption && variantOption.dataset.continueSelling === 'true';
    const unmanaged = !variant.inventory_management;
    const inventory = Number(variant.inventory_quantity || 0);
    const orderable = isOrderable(form, variant);
    const maximum = preorder || continueSelling || unmanaged
      ? 9999
      : Math.max(1, inventory);

    if (quantityInput) {
      quantityInput.dataset.variantId = String(variant.id);
      quantityInput.max = String(maximum);
      quantityInput.disabled = !orderable;

      if (Number(quantityInput.value || 1) > maximum) {
        quantityInput.value = String(maximum);
      }
    }

    if (submitQuantity) {
      submitQuantity.value = quantityInput ? quantityInput.value || '1' : '1';
    }

    form
      .querySelectorAll('[data-quantity-increment], [data-quantity-decrement]')
      .forEach(function (button) {
        button.disabled = !orderable;
      });
  }

  function restoreFixedProperties(form) {
    form.querySelectorAll('[data-fixed-value]').forEach(function (input) {
      input.value = input.dataset.fixedValue;
    });
  }

  function populateFormData(form, formData) {
    const state = states.get(form);
    const variant =
      state && getVariantById(state.variants, state.currentVariantId);
    const quantity = form.querySelector('[data-quantity-input]');

    if (variant) {
      formData.set('id', String(variant.id));
    }

    formData.set('quantity', quantity ? quantity.value || '1' : '1');

    form.querySelectorAll('[data-fixed-value]').forEach(function (input) {
      formData.set(input.name, input.dataset.fixedValue);
    });

    if (variant && isPreorder(form, variant)) {
      formData.set(
        'properties[Preorder Item]',
        'Ships when available. Contact us for ETA.',
      );
    } else {
      formData.delete('properties[Preorder Item]');
    }
  }

  function updatePurchaseState(form, variant) {
    const preorder = isPreorder(form, variant);
    const orderable = isOrderable(form, variant);
    const button = form.querySelector('[data-add-to-cart]');
    const badge = form.querySelector('[data-preorder-badge]');
    const message = form.querySelector('[data-preorder-message]');
    const property = form.querySelector('[data-preorder-property]');

    if (button) {
      const buttonText = !orderable
        ? 'Sold Out'
        : preorder
          ? 'Preorder'
          : (window.theme &&
              window.theme.strings &&
              window.theme.strings.product &&
              window.theme.strings.product.addToCart) ||
            'Add To Cart';

      if (button.disabled !== !orderable) {
        button.disabled = !orderable;
      }

      if (button.textContent.trim() !== buttonText) {
        button.textContent = buttonText;
      }

      button.dataset.preorderState = String(preorder);
      button.dataset.currentVariantId = String(variant.id);
    }

    if (badge) {
      badge.hidden = !preorder;
    }

    if (message) {
      message.classList.toggle('hidden', !preorder);
    }

    if (property) {
      const propertyValue = preorder
        ? 'Ships when available. Contact us for ETA.'
        : '';

      if (property.disabled !== !preorder) {
        property.disabled = !preorder;
      }

      if (property.value !== propertyValue) {
        property.value = propertyValue;
      }
    }

    updateOnlineAvailability(form, variant, preorder);
    updateQuantity(form, variant, preorder);
    restoreFixedProperties(form);
  }

  function dispatchVariantChange(form, variant) {
    form.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant: variant,
          currentVariant: variant,
          selectedVariant: variant,
          variantId: String(variant.id),
          variant_id: String(variant.id),
        },
      }),
    );
  }

  function applyVariant(form, variant, options) {
    if (!variant) {
      return;
    }

    const state = states.get(form);
    const root = getRoot(form);
    const previousId = state.currentVariantId;
    const nextId = String(variant.id);
    const values = variantOptions(variant);
    const variantSelect = form.querySelector(VARIANT_SELECT_SELECTOR);

    getGroups(form).forEach(function (group, index) {
      setGroupValue(group, values[index]);
    });

    if (variantSelect) {
      variantSelect.value = nextId;
    }

    state.currentVariantId = nextId;
    updateOptionAvailability(form, variant);
    updateSku(root, variant);
    updatePrice(root, variant);
    updatePurchaseState(form, variant);

    if (!options || options.updateUrl !== false) {
      updateUrl(variant);
    }

    if (previousId !== nextId || (options && options.forceEvent)) {
      if (variantSelect) {
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      dispatchVariantChange(form, variant);
    }
  }

  function getInitialVariant(form, variants) {
    const variantFromUrl = new URL(window.location.href).searchParams.get(
      'variant',
    );
    const hiddenSelect = form.querySelector(VARIANT_SELECT_SELECTOR);

    return (
      getVariantById(variants, variantFromUrl) ||
      getVariantById(variants, hiddenSelect && hiddenSelect.value) ||
      variants.find(function (variant) {
        return variant.available;
      }) ||
      variants[0]
    );
  }

  function initializeForm(form) {
    if (form.dataset.variantControllerInitialized === 'true') {
      return;
    }

    const data = form.querySelector(VARIANT_DATA_SELECTOR);
    let variants = [];

    try {
      variants = data ? JSON.parse(data.textContent) : [];
    } catch (error) {
      console.error('Unable to initialize product variants:', error);
      return;
    }

    if (!variants.length) {
      return;
    }

    form.dataset.variantControllerInitialized = 'true';
    const state = { variants: variants, currentVariantId: null };

    states.set(form, state);

    form.addEventListener('formdata', function (event) {
      populateFormData(form, event.formData);
    });

    applyVariant(form, getInitialVariant(form, variants), {
      updateUrl: false,
      forceEvent: true,
    });

    const button = form.querySelector('[data-add-to-cart]');

    if (button) {
      const buttonObserver = new MutationObserver(function () {
        const variant = getVariantById(
          state.variants,
          state.currentVariantId,
        );

        if (variant) {
          updatePurchaseState(form, variant);
        }
      });

      buttonObserver.observe(button, {
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
      state.buttonObserver = buttonObserver;
    }
  }

  function initializeForms(root) {
    const scope = root || document;
    scope.querySelectorAll(FORM_SELECTOR).forEach(initializeForm);
  }

  document.addEventListener('change', normalizeSelectionBeforeThemeHandler, true);

  document.addEventListener(
    'click',
    function (event) {
      const input = event.target.closest(
        'input[type="radio"][data-variant-option-input]',
      );

      if (!input || input.disabled) {
        return;
      }

      const form = input.closest(FORM_SELECTOR);
      const variant = form && resolveVariantForInput(form, input);

      if (variant) {
        applyVariant(form, variant, { updateUrl: true });
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  document.addEventListener('change', function (event) {
    const input = event.target.closest(OPTION_INPUT_SELECTOR);

    if (input) {
      const form = input.closest(FORM_SELECTOR);
      const variant = form && resolveVariantForInput(form, input);

      applyVariant(form, variant, { updateUrl: true });
      return;
    }

    const quantity = event.target.closest('[data-quantity-input]');

    if (quantity) {
      const form = quantity.closest(FORM_SELECTOR);
      const submitQuantity = form && form.querySelector('[data-submit-quantity]');

      if (submitQuantity) {
        submitQuantity.value = quantity.value || '1';
      }
    }
  });

  document.addEventListener('input', function (event) {
    const quantity = event.target.closest('[data-quantity-input]');

    if (!quantity) {
      return;
    }

    const form = quantity.closest(FORM_SELECTOR);
    const submitQuantity = form && form.querySelector('[data-submit-quantity]');

    if (submitQuantity) {
      submitQuantity.value = quantity.value || '1';
    }
  });

  document.addEventListener('click', function (event) {
    const quantityButton = event.target.closest(
      '[data-quantity-increment], [data-quantity-decrement]',
    );

    if (!quantityButton) {
      return;
    }

    const form = quantityButton.closest(FORM_SELECTOR);

    window.requestAnimationFrame(function () {
      const quantity = form && form.querySelector('[data-quantity-input]');
      const submitQuantity = form && form.querySelector('[data-submit-quantity]');

      if (quantity && submitQuantity) {
        submitQuantity.value = quantity.value || '1';
      }
    });
  });

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target.closest(FORM_SELECTOR);

      if (!form) {
        return;
      }

      const quantity = form.querySelector('[data-quantity-input]');
      const submitQuantity = form.querySelector('[data-submit-quantity]');
      const state = states.get(form);
      const variant =
        state && getVariantById(state.variants, state.currentVariantId);

      if (submitQuantity) {
        submitQuantity.value = quantity ? quantity.value || '1' : '1';
      }

      restoreFixedProperties(form);

      if (variant) {
        const property = form.querySelector('[data-preorder-property]');
        const preorder = isPreorder(form, variant);

        if (property) {
          property.disabled = !preorder;
          property.value = preorder
            ? 'Ships when available. Contact us for ETA.'
            : '';
        }
      }
    },
    true,
  );

  document.addEventListener('shopify:section:load', function (event) {
    initializeForms(event.target);
  });

  window.addEventListener('pageshow', function () {
    document.querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      const state = states.get(form);

      if (!state) {
        initializeForm(form);
        return;
      }

      applyVariant(form, getInitialVariant(form, state.variants), {
        updateUrl: false,
        forceEvent: false,
      });
    });
  });

  initializeForms(document);
})();
